import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { getVisitorId } from "@/lib/saveContact";

const API = process.env.REACT_APP_BACKEND_URL;

// Attach the customer bearer token (when logged in) so the backend can associate
// AddToCart / ViewContent events to the logged-in user for the admin dashboard.
const authHeaders = () => {
  try {
    const t = localStorage.getItem("xd_customer_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
};

// Small helper: RFC4122-ish v4 UUID using crypto if available.
const uuidv4 = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Loads the Meta Pixel script once (if a pixel_id is configured in admin_settings) and
 * fires a PageView on every route change. Exposes window.audrivioTrack(event, data) that
 * ALSO relays the event server-side via the Conversions API (CAPI) using a shared event_id
 * so Meta can deduplicate — this bypasses ad-blockers for the events that matter most.
 * `Purchase` is intentionally *not* relayed here: the backend fires it authoritatively as
 * soon as an order is placed / paid.
 */
export default function MetaPixel() {
  const location = useLocation();

  // Bootstrap: fetch pixel_id and inject the FB base snippet if configured
  useEffect(() => {
    let cancelled = false;
    // GA4 mirror — shared by both the sync fallback and the rich Pixel handler below.
    // Called on every audrivioTrack() so the GA4 funnel matches Meta's 1:1 whenever
    // GA4 is configured, regardless of whether Meta Pixel is enabled.
    const mirrorToGA4 = (event, payload) => {
      if (typeof window === "undefined" || typeof window.gtag !== "function") return;
      const ga4Map = {
        ViewContent: "view_item",
        AddToCart: "add_to_cart",
        InitiateCheckout: "begin_checkout",
        Search: "search",
        AddToWishlist: "add_to_wishlist",
        Contact: "generate_lead",
      };
      const gaName = ga4Map[event];
      if (!gaName) return;
      const gaParams = {
        currency: payload.currency || "EUR",
        value: payload.value,
      };
      if (Array.isArray(payload.content_ids)) {
        gaParams.items = payload.content_ids.map((id) => ({
          item_id: id,
          item_name: payload.content_name || undefined,
          price: payload.value && payload.content_ids.length ? payload.value / payload.content_ids.length : undefined,
          quantity: 1,
        }));
      }
      if (event === "Search" && payload.search_string) gaParams.search_term = payload.search_string;
      // generate_lead: expose which contact channel was used so GA4 can split the funnel
      // (whatsapp / email / phone / form) and attribute per-source lead volume. GA4
      // recommends `method` as the canonical param name for lead-origin reporting.
      if (event === "Contact") {
        if (payload.channel) gaParams.method = payload.channel;
        if (payload.source) gaParams.contact_source = payload.source;
      }
      try { window.gtag("event", gaName, gaParams); } catch { /* ignore */ }
    };

    // Install the server-only fallback SYNCHRONOUSLY so any early call site (e.g. a fast
    // AddToCart click during initial paint) always has a working relay. It will be replaced
    // by the richer client+server version once the pixel loads.
    if (!window.audrivioTrack) {
      window.audrivioTrack = (event, data) => {
        const eventId = (data && data.event_id) || uuidv4();
        const payload = { ...(data || {}), visitor_id: getVisitorId(), event_id: eventId };
        mirrorToGA4(event, payload);
        if (event === "Purchase") return; // backend fires this authoritatively
        axios.post(`${API}/api/track/event`, {
          event_name: event,
          event_id: eventId,
          custom_data: payload,
          event_source_url: typeof window !== "undefined" ? window.location.href : null,
        }, { headers: authHeaders() }).catch(() => {});
      };
    }
    axios.get(`${API}/api/public/site-config`).then((r) => {
      if (cancelled) return;
      const pixelId = r.data?.meta_pixel_id;
      if (!pixelId || window.__audrivio_meta_pixel_loaded) return;
      window.__audrivio_meta_pixel_loaded = true;
      /* eslint-disable */
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n;
        n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
        t = b.createElement(e); t.async = !0;
        t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
      }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      /* eslint-enable */
      window.fbq("init", pixelId);
      window.fbq("track", "PageView");
      // Rich helper: fires the client Pixel and also relays to CAPI (same event_id for dedup).
      window.audrivioTrack = (event, data) => {
        const eventId = (data && data.event_id) || uuidv4();
        // Attach persistent attribution context (Facebook click id + UTM) so the
        // server-side tracking dashboard can report which channel each event came from.
        const params = new URLSearchParams(window.location.search);
        const attribution = { visitor_id: getVisitorId() };
        const fbclid = params.get("fbclid");
        if (fbclid) { attribution.fbclid = fbclid; try { sessionStorage.setItem("audrivio_fbclid", fbclid); } catch (e) { /* storage disabled */ } }
        else { try { const s = sessionStorage.getItem("audrivio_fbclid"); if (s) attribution.fbclid = s; } catch (e) { /* storage disabled */ } }
        ["utm_source","utm_medium","utm_campaign","utm_term","utm_content"].forEach((k) => {
          const v = params.get(k);
          if (v) { attribution[k] = v; try { sessionStorage.setItem(`audrivio_${k}`, v); } catch (e) { /* storage disabled */ } }
          else { try { const s = sessionStorage.getItem(`audrivio_${k}`); if (s) attribution[k] = s; } catch (e) { /* storage disabled */ } }
        });
        const payload = { ...(data || {}), ...attribution, event_id: eventId };
        if (window.fbq) window.fbq("track", event, payload, { eventID: eventId });
        mirrorToGA4(event, payload);
        // Never relay Purchase (backend is authoritative to keep totals accurate).
        if (event !== "Purchase") {
          axios.post(`${API}/api/track/event`, {
            event_name: event,
            event_id: eventId,
            custom_data: payload,
            event_source_url: typeof window !== "undefined" ? window.location.href : null,
          }, { headers: authHeaders() }).catch(() => {});
        }
      };
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Route change → PageView (client only; CAPI PageView is low-signal, we skip the relay).
  useEffect(() => {
    if (window.fbq) window.fbq("track", "PageView");
  }, [location.pathname]);

  return null;
}
